// src/pages/admin/AdminWorkflows.jsx
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import {
  collection, getDocs, doc, setDoc, updateDoc,
  deleteDoc, orderBy, query, serverTimestamp,
} from 'firebase/firestore';
import {
  Field, TextInput, TextArea, PrimaryBtn, GhostBtn,
  Toast, ConfirmDialog, TagEditor,
  AdminCard, AdminHeading,
} from '../../components/AdminUI';
import FileDropZone from '../../components/FileDropZone';
import RichTextEditor from '../../components/RichTextEditor';

function blank() {
  return {
    id: '', order: 99, name: '', shortDescription: '',
    longDescription: '', images: [], coverImage: '',
    tags: [], nodeCount: 0, downloadCount: 0, published: false,
  };
}

// True for image URLs, false for video URLs (mp4/webm/mov/avi) — used to
// keep the cover-image picker to stills only, since a video can't be used
// as a static gallery thumbnail.
function isImageUrl(src) {
  const p = src.split('?')[0].toLowerCase();
  return !/\.(mp4|webm|mov|avi)/.test(p);
}

function blankVariant() {
  return { id: Date.now().toString() + Math.random(), label: '', url: '' };
}

function WorkflowForm({ initial, onSave, onCancel, saving }) {
  const [wf, setWf] = useState(initial);
  const set = (k, v) => setWf(prev => ({ ...prev, [k]: v }));

  // Migrate old single downloadUrl → downloads array
  const [downloads, setDownloads] = useState(() => {
    if (initial.downloads?.length > 0) return initial.downloads;
    if (initial.downloadUrl) return [{ id: '0', label: 'Download workflow', url: initial.downloadUrl }];
    return [blankVariant()];
  });
  const [mediaUrls, setMediaUrls] = useState(initial.images || []);
  const [coverImage, setCoverImage] = useState(initial.coverImage || '');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const storageBase = `workflows/${wf.id || 'new'}`;

  const availableCovers = mediaUrls.filter(isImageUrl);

  // Keep the selected cover valid as the media gallery changes: if nothing
  // is selected yet, or the selected image was removed, fall back to the
  // first available still image (matches the public-site default).
  useEffect(() => {
    if (availableCovers.length === 0) { if (coverImage) setCoverImage(''); return; }
    if (!availableCovers.includes(coverImage)) setCoverImage(availableCovers[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaUrls]);

  function setVariantLabel(idx, label) {
    setDownloads(prev => prev.map((d, i) => i === idx ? { ...d, label } : d));
  }
  function setVariantUrl(idx, url) {
    setDownloads(prev => prev.map((d, i) => i === idx ? { ...d, url } : d));
  }
  function addVariant() { setDownloads(prev => [...prev, blankVariant()]); }
  function removeVariant(idx) { setDownloads(prev => prev.filter((_, i) => i !== idx)); }

  function handleSave() {
    setSubmitAttempted(true);
    if (!wf.name.trim()) return alert('Name is required.');
    if (!wf.id.trim())   return alert('ID (slug) is required.');
    const validDownloads = downloads.filter(d => d.url);
    if (validDownloads.length === 0) return alert('Please upload at least one workflow JSON file.');
    onSave({ ...wf, downloads: validDownloads, images: mediaUrls, coverImage });
  }

  return (
    <AdminCard>
      <AdminHeading>{initial.name ? `Edit — ${initial.name}` : 'New Workflow'}</AdminHeading>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        <Field label="ID (slug)" hint="Lowercase, no spaces. e.g. portrait-sdxl">
          <TextInput value={wf.id} onChange={v => set('id', v)} placeholder="portrait-sdxl" mono />
        </Field>
        <Field label="Display order" hint="Lower = appears first in list">
          <TextInput value={wf.order} onChange={v => set('order', Number(v))} type="number" mono />
        </Field>
      </div>

      <Field label="Name">
        <TextInput value={wf.name} onChange={v => set('name', v)} placeholder="Portrait SDXL + Refiner" />
      </Field>

      <Field label="Short description" hint="Shown in the list row (~120 chars)">
        <TextInput value={wf.shortDescription} onChange={v => set('shortDescription', v)}
          placeholder="One-line summary visible in the list." />
      </Field>

      <Field label="Long description / comments" hint="Supports bold, italic, headings, links and YouTube embeds.">
        <RichTextEditor
          value={wf.longDescription}
          onChange={v => set('longDescription', v)}
        />
      </Field>

      <Field label="Download variants" hint='Add one per version — e.g. "Comfy Cloud" and "Local / custom nodes"'>
        {downloads.map((d, idx) => (
          <div key={d.id} style={{ background: 'var(--sand-bg)', border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)' }}>VARIANT {String(idx + 1).padStart(2, '0')}</span>
              {downloads.length > 1 && (
                <button onClick={() => removeVariant(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--sand-hint)' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#fca5a5'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--sand-hint)'}
                >Remove ×</button>
              )}
            </div>
            <Field label="Label" hint='e.g. "Comfy Cloud version" or "Custom nodes (local)"'>
              <TextInput value={d.label} onChange={v => setVariantLabel(idx, v)} placeholder="Comfy Cloud version" />
            </Field>
            <Field label="JSON file" hint="Required · .json exported from ComfyUI">
              <FileDropZone
                storagePath={`${storageBase}/json`}
                accept="application/json,.json"
                multiple={false}
                label="Drop .json workflow file here"
                hint=".json · max 10 MB"
                existingUrls={d.url ? [d.url] : []}
                onUrlsChange={urls => setVariantUrl(idx, urls[0] || '')}
                required={submitAttempted && !d.url}
              />
            </Field>
          </div>
        ))}
        <GhostBtn onClick={addVariant}>+ Add another variant</GhostBtn>
      </Field>

      <Field label="Preview images & videos" hint="Optional · PNG, JPG, GIF, WebP, MP4">
        <FileDropZone
          storagePath={`${storageBase}/media`}
          accept="image/*,video/mp4,video/webm"
          multiple={true}
          label="Drop images or videos here"
          hint="PNG · JPG · GIF · WebP · MP4 · WebM · multiple files OK"
          existingUrls={mediaUrls}
          onUrlsChange={setMediaUrls}
        />
      </Field>

      {availableCovers.length > 0 && (
        <Field label="Gallery cover image" hint="Shown on the main Workflows gallery. Defaults to the first image above.">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {availableCovers.map((src, i) => {
              const selected = src === coverImage;
              return (
                <div
                  key={src}
                  onClick={() => setCoverImage(src)}
                  style={{
                    position: 'relative', width: 96, height: 96, borderRadius: 'var(--radius-sm)',
                    overflow: 'hidden', cursor: 'pointer',
                    border: `2px solid ${selected ? 'var(--sand-amber)' : 'var(--sand-border)'}`,
                    flexShrink: 0,
                  }}
                >
                  <img src={src} alt={`Option ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  {selected && (
                    <div style={{
                      position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: '50%',
                      background: 'var(--sand-amber)', color: '#0e1525', fontSize: 11, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>✓</div>
                  )}
                </div>
              );
            })}
          </div>
        </Field>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        <Field label="Node count">
          <TextInput value={wf.nodeCount} onChange={v => set('nodeCount', Number(v))} type="number" mono />
        </Field>
        <Field label="Download count">
          <TextInput value={wf.downloadCount} onChange={v => set('downloadCount', Number(v))} type="number" mono />
        </Field>
      </div>

      <Field label="Tags">
        <TagEditor tags={wf.tags} onChange={v => set('tags', v)} />
      </Field>

      <Field label="Published">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={wf.published}
            onChange={e => set('published', e.target.checked)}
            style={{ accentColor: 'var(--sand-amber)', width: 16, height: 16 }} />
          <span style={{ fontSize: 13, color: 'var(--sand-muted)' }}>Visible to public</span>
        </label>
      </Field>

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <PrimaryBtn onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save workflow'}
        </PrimaryBtn>
        <GhostBtn onClick={onCancel}>Cancel</GhostBtn>
      </div>
    </AdminCard>
  );
}

function WorkflowRow({ wf, onEdit, onDelete, onToggle, dragHandlers, isDragging, isDragOver }) {
  return (
    <div
      {...dragHandlers}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px',
        background: isDragOver ? 'var(--sand-border)' : 'var(--sand-surface)',
        border: `0.5px solid ${isDragOver ? 'var(--sand-amber)' : 'var(--sand-border)'}`,
        borderRadius: 'var(--radius-md)', marginBottom: 6,
        opacity: isDragging ? 0.4 : 1,
        cursor: 'grab', transition: 'background 0.1s, border-color 0.1s',
        userSelect: 'none',
      }}
    >
      <svg width="10" height="16" viewBox="0 0 10 16" fill="var(--sand-hint)" style={{ flexShrink: 0 }}>
        <circle cx="3" cy="3" r="1.5"/><circle cx="7" cy="3" r="1.5"/>
        <circle cx="3" cy="8" r="1.5"/><circle cx="7" cy="8" r="1.5"/>
        <circle cx="3" cy="13" r="1.5"/><circle cx="7" cy="13" r="1.5"/>
      </svg>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)', minWidth: 22 }}>
        {String(wf.order).padStart(2,'0')}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--sand-text)' }}>
          {wf.name}
          {!wf.published && (
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, padding: '1px 6px', borderRadius: 10, marginLeft: 6, background: '#0e1e30', color: '#3a5070', border: '0.5px solid #1e2d45' }}>draft</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--sand-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {wf.shortDescription}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <GhostBtn onClick={() => onToggle(wf)} small>{wf.published ? 'Unpublish' : 'Publish'}</GhostBtn>
        <GhostBtn onClick={() => onEdit(wf)} small>Edit</GhostBtn>
        <PrimaryBtn onClick={() => onDelete(wf)} danger small>Delete</PrimaryBtn>
      </div>
    </div>
  );
}

export default function AdminWorkflows() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null);
  const [creating, setCreating]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState({ msg: '', type: 'success' });
  const [confirm, setConfirm]     = useState(null);
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  }

  async function load() {
    setLoading(true);
    try {
      const q = query(collection(db, 'workflows'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      setWorkflows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      showToast('Failed to load workflows.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleSave(wf) {
    setSaving(true);
    try {
      await setDoc(doc(db, 'workflows', wf.id), { ...wf, updatedAt: serverTimestamp(), createdAt: wf.createdAt || serverTimestamp() });
      showToast('Workflow saved.');
      setEditing(null); setCreating(false);
      await load();
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(wf) {
    try {
      await deleteDoc(doc(db, 'workflows', wf.id));
      showToast('Workflow deleted.');
      setConfirm(null);
      await load();
    } catch (e) {
      showToast('Delete failed.', 'error');
    }
  }

  async function handleToggle(wf) {
    try {
      await updateDoc(doc(db, 'workflows', wf.id), { published: !wf.published, updatedAt: serverTimestamp() });
      showToast(wf.published ? 'Unpublished.' : 'Published.');
      await load();
    } catch (e) {
      showToast('Update failed.', 'error');
    }
  }

  function onDragStart(idx) { dragItem.current = idx; }
  function onDragEnter(idx) { dragOver.current = idx; }
  async function onDragEnd() {
    if (dragItem.current === null || dragOver.current === null) return;
    const reordered = [...workflows];
    const [moved] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOver.current, 0, moved);
    const updated = reordered.map((w, i) => ({ ...w, order: i + 1 }));
    setWorkflows(updated);
    dragItem.current = null; dragOver.current = null;
    try {
      await Promise.all(updated.map(w => updateDoc(doc(db, 'workflows', w.id), { order: w.order })));
      showToast('Order saved.');
    } catch (e) {
      showToast('Failed to save order.', 'error');
    }
  }

  if (creating) return <WorkflowForm initial={blank()} onSave={handleSave} onCancel={() => setCreating(false)} saving={saving} />;
  if (editing)  return <WorkflowForm initial={editing}  onSave={handleSave} onCancel={() => setEditing(null)}  saving={saving} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <AdminHeading>Workflows</AdminHeading>
        <PrimaryBtn onClick={() => setCreating(true)}>+ New workflow</PrimaryBtn>
      </div>
      <p style={{ fontSize: 12, color: 'var(--sand-hint)', fontStyle: 'italic', marginBottom: 16 }}>
        Drag rows to reorder. Changes save automatically to Firestore.
      </p>
      {loading ? (
        <p style={{ fontSize: 13, color: 'var(--sand-hint)' }}>Loading…</p>
      ) : workflows.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--sand-hint)', fontStyle: 'italic' }}>No workflows yet.</p>
      ) : (
        workflows.map((wf, idx) => (
          <WorkflowRow key={wf.id} wf={wf}
            onEdit={setEditing} onDelete={w => setConfirm(w)} onToggle={handleToggle}
            isDragging={dragItem.current === idx} isDragOver={dragOver.current === idx}
            dragHandlers={{
              draggable: true,
              onDragStart: () => onDragStart(idx),
              onDragEnter: () => onDragEnter(idx),
              onDragEnd: onDragEnd,
              onDragOver: e => e.preventDefault(),
            }}
          />
        ))
      )}
      {confirm && <ConfirmDialog message={`Delete "${confirm.name}"?`} onConfirm={() => handleDelete(confirm)} onCancel={() => setConfirm(null)} />}
      <Toast message={toast.msg} type={toast.type} />
    </div>
  );
}
